import { type ClientContext, ContextClient } from '~/services/contextClient';
import WorkflowsClientFactory from './workflowsClient.factory';
import type {
    CreateWorkflowInput,
    ListWorkflowsParams,
    UpdateDraftInput,
    WorkflowsClientInterface,
} from './workflowsClient.types';
import type { Workflow, DrainingVersion } from '~/types/workflow.types';
import type { AxiosPromise } from 'axios';

class WorkflowsClient extends ContextClient<WorkflowsClientInterface> {
    constructor() {
        super(WorkflowsClientFactory);
    }

    getWorkflows(context: ClientContext, params?: ListWorkflowsParams): AxiosPromise<Workflow[]> {
        return this.getClient(context).getWorkflows(params);
    }

    getWorkflow(context: ClientContext, workflowId: string, version?: number): AxiosPromise<Workflow> {
        return this.getClient(context).getWorkflow(workflowId, version);
    }

    getDependentWorkflows(context: ClientContext, workflowId: string): AxiosPromise<Workflow[]> {
        return this.getClient(context).getDependentWorkflows(workflowId);
    }

    createWorkflow(context: ClientContext, input: CreateWorkflowInput): AxiosPromise<Workflow> {
        return this.getClient(context).createWorkflow(input);
    }

    createDraft(context: ClientContext, workflowId: string): AxiosPromise<Workflow> {
        return this.getClient(context).createDraft(workflowId);
    }

    updateDraft(context: ClientContext, workflowId: string, input: UpdateDraftInput): AxiosPromise<Workflow> {
        return this.getClient(context).updateDraft(workflowId, input);
    }

    publishWorkflow(context: ClientContext, workflowId: string): AxiosPromise<Workflow> {
        return this.getClient(context).publishWorkflow(workflowId);
    }

    archiveWorkflow(context: ClientContext, workflowId: string): AxiosPromise<void> {
        return this.getClient(context).archiveWorkflow(workflowId);
    }

    restoreWorkflow(context: ClientContext, workflowId: string): AxiosPromise<void> {
        return this.getClient(context).restoreWorkflow(workflowId);
    }

    getWorkflowVersions(context: ClientContext, workflowId: string): AxiosPromise<Workflow[]> {
        return this.getClient(context).getWorkflowVersions(workflowId);
    }

    getDrainingVersions(context: ClientContext, workflowId: string): AxiosPromise<DrainingVersion[]> {
        return this.getClient(context).getDrainingVersions(workflowId);
    }
}

export default new WorkflowsClient();
